import React, { useState, forwardRef, useImperativeHandle } from "react";
import Button from "components/BaseWidgets/button";
import Password from "components/BaseWidgets/form/password";
import CardLayout from "components/Settings/components/CardLayout";

const ChangePassword = forwardRef((props, ref) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validatePasswordStrength = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long.";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter.";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter.";
    }
    if (!hasNumber) {
      return "Password must contain at least one number.";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character.";
    }

    return null;
  };

  const validateField = (fieldName, value) => {
    let error = null;

    switch (fieldName) {
      case "currentPassword":
        if (value && !value.trim()) {
          error = "Current password is required.";
        }
        break;

      case "newPassword":
        if (value) {
          const strengthError = validatePasswordStrength(value);
          if (strengthError) {
            error = strengthError;
          } else if (currentPassword && value === currentPassword) {
            error = "New password cannot be the same as current password.";
          }
        }
        break;

      case "confirmPassword":
        if (value) {
          const strengthError = validatePasswordStrength(value);
          if (strengthError) {
            error = strengthError;
          } else if (newPassword && value !== newPassword) {
            error = "Passwords do not match.";
          }
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
    validate: () => {
      const newErrors = {};

      if (!currentPassword) {
        newErrors.currentPassword = "Current password is required.";
      }

      if (!newPassword) {
        newErrors.newPassword = "New password is required.";
      } else {
        const strengthError = validatePasswordStrength(newPassword);
        if (strengthError) {
          newErrors.newPassword = strengthError;
        } else if (currentPassword && newPassword === currentPassword) {
          newErrors.newPassword =
            "New password cannot be the same as current password.";
        }
      }

      if (!confirmPassword) {
        newErrors.confirmPassword = "Confirm password is required.";
      } else {
        const strengthError = validatePasswordStrength(confirmPassword);
        if (strengthError) {
          newErrors.confirmPassword = strengthError;
        } else if (newPassword && confirmPassword !== newPassword) {
          newErrors.confirmPassword = "Passwords do not match.";
        }
      }

      setErrors(newErrors);
      setTouched({
        currentPassword: true,
        newPassword: true,
        confirmPassword: true,
      });
      return Object.keys(newErrors).length === 0;
    },
  }));

  const handleCurrentPasswordChange = (e) => {
    const value = e.target.value;
    setCurrentPassword(value);
    setTouched((prev) => ({ ...prev, currentPassword: true }));
    validateField("currentPassword", value);

    if (newPassword) {
      validateField("newPassword", newPassword);
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setTouched((prev) => ({ ...prev, newPassword: true }));
    validateField("newPassword", value);

    if (confirmPassword) {
      validateField("confirmPassword", confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    validateField("confirmPassword", value);
  };

  return (
    <CardLayout title="Change password">
      <div className="flex flex-col gap-4 password-wrapper">
        <div className="flex flex-col gap-1-5">
          <Password
            id="current-password"
            label="Current password"
            value={currentPassword}
            onChange={handleCurrentPasswordChange}
            labelClass={`label fw-600`}
          />
          {touched.currentPassword && errors.currentPassword && (
            <p className="text-red-500 text-sm mt-1 warning-message">
              {errors.currentPassword}
            </p>
          )}
          <Button
            href={"/forgot"}
            className="forgot-password-btn"
            variant="link"
          >
            Forgot password?
          </Button>
        </div>

        <div className="flex flex-col gap-1-5">
          <Password
            id="new-password"
            label="New password"
            value={newPassword}
            onChange={handleNewPasswordChange}
            labelClass={`label fw-600`}
          />
          {touched.newPassword && errors.newPassword && (
            <p className="text-red-500 text-sm mt-1 warning-message">
              {errors.newPassword}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1-5">
          <Password
            id="confirm-password"
            label="Confirm password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            labelClass={`label fw-600`}
          />
          {touched.confirmPassword && errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1 warning-message">
              {errors.confirmPassword}
            </p>
          )}
        </div>
      </div>
    </CardLayout>
  );
});

export default ChangePassword;
